import { Pencil } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { useImmer } from "use-immer";
import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Box, Stack, useTheme } from "@mui/material";

const MAX_NAME_LENGTH = 255;

interface WorkspaceTitleProps {
  name: string;
  /**
   * Renaming, when the entity owns its own name.
   *
   * `null` renders the name as static text. That is not the same as
   * `readOnly` — a Custom Shipping Provider's name belongs to the parent
   * experiment form, so there is no rename to perform here at all, whereas an
   * entity the viewer may not rename has one that is merely unavailable.
   */
  onRename: ((name: string) => Promise<void> | void) | null;
  /** Rename exists for this entity but is currently unavailable (no rights). */
  readOnly?: boolean;
  saving?: boolean;
  /**
   * Content read as part of the name (e.g. an entity's surface chips), laid
   * out 16px after it and BEFORE the rename pencil. The pencil is always laid
   * out, so anything placed after the whole title would sit behind the space
   * it reserves; here that space falls at the end of the row instead.
   */
  trailing?: ReactNode;
}

/**
 * The entity's name at the top of an embedded workspace page.
 *
 * When it is renameable, it is edited where it is read rather than in a
 * settings pane: clicking the title (or the pencil that appears on hover) turns
 * it into an input. Enter or blur commits, Escape reverts. An empty value is
 * not a rename — it restores the previous name, because an entity with no name
 * is unreachable in its list.
 */
export const WorkspaceTitle = ({
  name,
  onRename,
  readOnly = false,
  saving = false,
  trailing,
}: WorkspaceTitleProps) => {
  const theme = useTheme();
  const [editing, setEditing] = useImmer(false);
  const [draft, setDraft] = useImmer(name);
  // Escape must win over the blur it triggers, so the commit path consults this
  // rather than racing two handlers against the same input.
  const revertingRef = useRef(false);

  useEffect(() => {
    if (!editing) {
      setDraft(name);
    }
  }, [name, editing, setDraft]);

  const canEdit = onRename !== null && !readOnly;

  const commit = async () => {
    if (revertingRef.current) {
      revertingRef.current = false;
      return;
    }
    const trimmed = draft.trim();
    setEditing(false);
    if (!trimmed || trimmed === name || onRename === null) {
      setDraft(name);
      return;
    }
    await onRename(trimmed);
  };

  if (editing) {
    return (
      <IgTextField
        autoFocus
        fullWidth
        label="Name"
        disabled={saving}
        slotProps={{ htmlInput: { maxLength: MAX_NAME_LENGTH } }}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void commit();
          }
          if (event.key === "Escape") {
            revertingRef.current = true;
            setDraft(name);
            setEditing(false);
          }
        }}
        sx={{ maxWidth: 520 }}
      />
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        minWidth: 0,
        // The pencil is always laid out and merely transparent, so revealing it
        // on hover never reflows the title next to it.
        "&:hover .WorkspaceTitle-pencil": { opacity: canEdit ? 1 : 0 },
      }}
    >
      <IgTypography
        component="h1"
        variant="h1"
        onClick={canEdit ? () => setEditing(true) : undefined}
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: canEdit ? "text" : "default",
          borderRadius: "6px",
          // H3/Semi Bold: 16px on a 24px line.
          fontSize: "16px",
          fontWeight: 600,
          lineHeight: "24px",
          px: 0.5,
          mx: -0.5,
          "&:hover": canEdit
            ? { bgcolor: theme.igBackgroundColors.interactive.neutral.hover }
            : undefined,
        }}
      >
        {name}
      </IgTypography>
      {trailing ? <Box sx={{ ml: 4, minWidth: 0 }}>{trailing}</Box> : null}
      {onRename !== null ? (
        <IgIconButton
          className="WorkspaceTitle-pencil"
          aria-label="Rename"
          size="small"
          disabled={readOnly}
          onClick={() => setEditing(true)}
          sx={{ ml: 0.5, opacity: 0, transition: "opacity 120ms ease" }}
        >
          <Pencil size={15} />
        </IgIconButton>
      ) : null}
    </Stack>
  );
};
